import * as angular from "angular";
import swal from "sweetalert2";
import { FORMS_PENDING_REQUESTS } from "../../../routes";

const FIVE_MINUTES = 5 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;

export function parseRefreshInterval(value) {
	// .NET's invariant TimeSpan format: [d.]hh:mm:ss[.fffffff].
	const parts = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/.exec(value ?? "");
	if(!parts) {
		return undefined;
	}
	const [, days, hours, minutes, seconds, fraction] = parts;
	if(Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) {
		return undefined;
	}
	const milliseconds = ((Number(days ?? 0) * 24 + Number(hours)) * 60 * 60
		+ Number(minutes) * 60 + Number(seconds) + Number(`0.${fraction ?? 0}`)) * 1000;
	return Number.isFinite(milliseconds) && milliseconds > 0 ? Math.ceil(milliseconds) : undefined;
}

export function getPollingDelay(interval, elapsed) {
	if(elapsed < FIVE_MINUTES) {
		return Math.min(interval, FIVE_MINUTES - elapsed);
	}
	if(elapsed < ONE_HOUR) {
		return Math.min(FIVE_MINUTES, ONE_HOUR - elapsed);
	}
	return ONE_HOUR;
}

export function OnboardingRequests(
	api,
	$userProfile,
	$timeout,
	$q,
	formsService,
	appState,
	loadingService,
	$state,
	toastr
) {
	this.create = (form, onRequests, { includeProcessed = false } = {}) => {
		const interval = parseRefreshInterval(form.Onboarding?.Requests?.RefreshInterval);
		const userId = $userProfile.getUser().databaseId;
		let disposed = false;
		let timer;
		let startedAt;
		let pendingIds = new Set();
		let inFlight;
		let hasLoaded = false;
		let awaitingAccess = false;
		let hadAccess = appState.forms.selected?.Id === form.Id || appState.forms.items.some(item => item.Id === form.Id);

		function clearTimer() {
			$timeout.cancel(timer);
			timer = undefined;
		}

		function schedule() {
			clearTimer();
			if(hasLoaded && pendingIds.size === 0 && !awaitingAccess) {
				startedAt = undefined;
			}
			if(disposed || interval === undefined || (hasLoaded && pendingIds.size === 0 && !awaitingAccess)) {
				return;
			}
			startedAt = startedAt ?? Date.now();
			timer = $timeout(() => refresh().catch(() => undefined), getPollingDelay(interval, Date.now() - startedAt));
			// Angular rejects cancelled timers; cancellation is normal on selection changes.
			timer.catch(() => undefined);
		}

		function handleApproval() {
			return $q.when(swal.fire({
				title: "Request Processed",
				text: `Your request for ${form.Name || form.Code} has been processed. Open the form now?`,
				type: "success",
				showCancelButton: true,
				confirmButtonColor: "#8CC541",
				confirmButtonText: `Open ${form.Name || form.Code}`,
				cancelButtonText: "Later"
			})).then(result => {
				if(disposed || !result.value) {
					return;
				}
				const openForm = formsService.refreshForms().then(forms => {
					if(disposed) {
						return;
					}
					const approvedForm = forms.find(item => item.Id === form.Id);
					if(!approvedForm) {
						throw new Error("Approved form is unavailable");
					}
					return appState.forms.selected?.Id === form.Id
						? appState.reloadForm()
						: appState.forms.select(approvedForm);
				})
					.then(selected => {
						if(disposed) {
							return;
						}
						// loadForm can resolve undefined on failure. Do not navigate with an unloaded form.
						if(selected?.Id !== form.Id) {
							throw new Error("Approved form could not be loaded");
						}
						return $state.go("authenticated.dashboard");
					});
				return loadingService.watch(openForm).catch(() => {
					if(!disposed) {
						toastr.error("Unable to open the approved form. Please select it from the form picker or try again.");
					}
				});
			});
		}

		function fetchRequests() {
			return $q.when(api.get(FORMS_PENDING_REQUESTS(form.Id, userId))).then(requests => {
				if(disposed) {
					return;
				}
				const processed = requests.filter(request => pendingIds.has(request.Id) && request.IsProcessed === true);
				const explicitlyApproved = processed.some(request => request.Status?.Code === "PROCESSED" && request.Status.IsRejected !== true);
				const mayBeApproved = interval !== undefined && (awaitingAccess || processed.some(request =>
					request.Status?.IsRejected !== true && (!request.Status?.Code || request.Status.Code === "PROCESSED")
				));
				// If Status is omitted, newly available access confirms approval; pre-existing access does not.
				const access = mayBeApproved ? formsService.refreshForms() : $q.when([]);
				return access.then(forms => {
					if(disposed) {
						return;
					}
					pendingIds = new Set(requests.filter(request => request.IsProcessed !== true).map(request => request.Id));
					hasLoaded = true;
					onRequests(includeProcessed ? requests : requests.filter(request => request.IsProcessed !== true));
					const hasAccess = forms.some(item => item.Id === form.Id);
					const approved = explicitlyApproved || awaitingAccess || (!hadAccess && hasAccess);
					// Keep a confirmed processing transition until access is visible in the forms API.
					awaitingAccess = mayBeApproved && !hasAccess && (explicitlyApproved || awaitingAccess);
					if(mayBeApproved) {
						hadAccess = hasAccess;
					}
					if(mayBeApproved && hasAccess && (approved || appState.forms.selected?.Id === form.Id)) {
						handleApproval().catch(() => {
							if(!disposed) {
								toastr.error("Unable to refresh form access. Please reload the page.");
							}
						});
					}
				});
			})
				.finally(schedule);
		}

		function refresh(afterSubmission = false) {
			// A submission during an older fetch needs a fresh response after that fetch completes.
			if(inFlight) {
				return inFlight.catch(() => undefined).then(() => refresh(afterSubmission));
			}
			if(disposed) {
				return $q.when();
			}
			if(afterSubmission) {
				// Retry a failed first reload even before the new request's ID is known.
				hasLoaded = false;
				if(pendingIds.size === 0) {
					startedAt = undefined;
				}
			}
			clearTimer();
			inFlight = fetchRequests().finally(() => {
				inFlight = undefined;
			});
			return inFlight;
		}

		return {
			refresh,
			dispose() {
				disposed = true;
				clearTimer();
			}
		};
	};
}

export default angular.module("vesl.screens.welcome.onboarding-requests", [])
	.service("onboardingRequests", OnboardingRequests).name;
